/**
 * Pure helpers for personal table layouts (Чат 4, правка №15). [Claude] 2026-11-02
 *
 * A column definition:
 *   { id, label, value(row) -> raw value used for sort/search, render(row) -> node,
 *     width (default px), pinned (never hidden; columns up to it stick to the left), sortable (default true) }
 * Personal prefs (stored on the server, /v1/ui-preferences/<key>):
 *   { columns: [ids in order] (empty = defaults), widths: {id: px}, sort: {key, dir} | null }
 */

export const MIN_WIDTH = 60;
export const MAX_WIDTH = 800;

export const emptyTablePrefs = () => ({ columns: [], widths: {}, sort: null });

/** Visible column ids in display order (the user's choice or defaults); pinned columns are never dropped. */
export function visibleColumnIds(catalog, prefs, defaultIds) {
    const known = new Set(catalog.map((c) => c.id));
    const chosen = prefs?.columns?.length ? prefs.columns : defaultIds;
    const out = [];
    for (const id of chosen || []) {
        if (known.has(id) && !out.includes(id)) out.push(id);
    }
    const missingPinned = catalog.filter((c) => c.pinned && !out.includes(c.id)).map((c) => c.id);
    return [...missingPinned, ...out];
}

/** Columns that stick to the left while scrolling sideways: everything up to the last pinned one (max 3). */
export function stickyCount(columns) {
    let last = -1;
    columns.forEach((c, i) => { if (c.pinned) last = i; });
    return Math.min(Math.max(last + 1, 1), 3);
}

export function resolveColumns(catalog, prefs, defaultIds) {
    const byId = new Map(catalog.map((c) => [c.id, c]));
    return visibleColumnIds(catalog, prefs, defaultIds).map((id) => byId.get(id));
}

export function columnWidth(column, prefs) {
    const px = Number(prefs?.widths?.[column.id]);
    if (Number.isFinite(px) && px > 0) return clampWidth(px);
    return column.width || 160;
}

export const clampWidth = (px) => Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(px)));

/** Next sort state when a header is clicked: asc → desc → none. */
export function nextSort(sort, key) {
    if (!sort || sort.key !== key) return { key, dir: "asc" };
    if (sort.dir === "asc") return { key, dir: "desc" };
    return null;
}

const isEmpty = (v) => v === null || v === undefined || v === "" || (typeof v === "number" && Number.isNaN(v));

function compareValues(a, b) {
    if (typeof a === "number" && typeof b === "number") return a - b;
    if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
    return String(a).localeCompare(String(b), "ru", { numeric: true, sensitivity: "base" });
}

/** Stable sort; empty values always go last regardless of direction. */
export function sortRows(rows, column, dir) {
    if (!column || !dir) return rows;
    const get = column.value || ((row) => row[column.id]);
    const factor = dir === "desc" ? -1 : 1;
    return rows
        .map((row, index) => ({ row, index, v: get(row) }))
        .sort((x, y) => {
            const ex = isEmpty(x.v);
            const ey = isEmpty(y.v);
            if (ex && ey) return x.index - y.index;
            if (ex) return 1;
            if (ey) return -1;
            return compareValues(x.v, y.v) * factor || x.index - y.index;
        })
        .map((item) => item.row);
}

/** Prefs after the user changes the set/order of visible columns. */
export function withColumns(prefs, catalog, ids) {
    const known = new Set(catalog.map((c) => c.id));
    return { ...emptyTablePrefs(), ...prefs, columns: ids.filter((id) => known.has(id)) };
}

export function withWidth(prefs, id, px) {
    return { ...emptyTablePrefs(), ...prefs, widths: { ...(prefs?.widths || {}), [id]: clampWidth(px) } };
}

export function withSort(prefs, sort) {
    return { ...emptyTablePrefs(), ...prefs, sort };
}
